import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import { resolveAssetUrl } from "../../api/config";
import SearchableSelect from "../../components/common/SearchableSelect";
import Swal from "sweetalert2";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";
import { FaFileExcel } from "react-icons/fa";
import { exportToExcel } from "../../components/common/exportUtils";

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxButtons = 5;
  if (totalPages <= maxButtons) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (currentPage <= 2) {
      end = 3;
    } else if (currentPage >= totalPages - 1) {
      start = totalPages - 2;
    }
    if (start > 2) {
      pages.push("...");
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < totalPages - 1) {
      pages.push("...");
    }
    pages.push(totalPages);
  }
  return pages;
};

const formatAgeCategoryLabel = (value) => {
  const token = String(value || "").toLowerCase();
  if (token === "sub_junior") return "Sub-Junior";
  if (token === "junior") return "Junior";
  if (token === "senior") return "Senior";
  return token ? token : "Unknown";
};

const gameLabel = (game) => {
  const id = game?.id ?? game?.game_id;
  const label = String(
    game?.game_name ||
      game?.name ||
      game?.title ||
      game?.label ||
      game?.text ||
      game?.event_game ||
      game?.game ||
      (id ? `Game #${id}` : "")
  ).trim();
  return label || (id ? `Game #${id}` : "—");
};

const normalizeGameOptions = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const byId = new Map();

  list.forEach((row) => {
    const rawId = row?.id ?? row?.game_id ?? row?.value;
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return;

    const option = {
      ...row,
      id,
      game_id: id,
      game_name: gameLabel({ ...row, id }),
    };
    byId.set(String(id), option);
  });

  return Array.from(byId.values()).sort((a, b) =>
    gameLabel(a).localeCompare(gameLabel(b), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};

const batchRequests = async (items, fn, limit = 5) => {
  const results = [];
  for (let i = 0; i < items.length; i += limit) {
    const chunk = items.slice(i, i + limit);
    results.push(...(await Promise.all(chunk.map(fn))));
  }
  return results;
};

const parseGames = (row) => {
  if (!row) return [];
  if (Array.isArray(row.games) && row.games.length > 0) {
    return row.games;
  }
  const concat = row.games_concat || row.game_concat || "";
  if (concat && typeof concat === "string") {
    const parts = concat.split("||").filter(Boolean);
    const parsed = parts
      .map((part) => {
        const pieces = part.split("::");
        const reg_id = pieces[0] ? Number(pieces[0]) : row.registration_id;
        const game_id = pieces[1] ? Number(pieces[1]) : null;
        const game_name = pieces[2] ? pieces[2].trim() : "";
        return {
          reg_id,
          game_id,
          game_name: game_name || (game_id ? `Game #${game_id}` : "—"),
        };
      })
      .filter((g) => g.game_name && g.game_name !== "—");
    if (parsed.length > 0) return parsed;
  }
  if (row.game_name && String(row.game_name).trim()) {
    return [{ reg_id: row.registration_id, game_id: row.game_id, game_name: String(row.game_name).trim() }];
  }
  return [];
};

const registrationFeeBreakdown = (row) => {
  const isFeeExempt = Boolean(Number(row?.is_fee_exempt || 0));
  const escortCount = [row?.escort_name, row?.escort_name_2]
    .filter((name) => String(name || "").trim() !== "").length;
  const registrationFee = isFeeExempt ? 0 : Number(row?.registration_fee || 0);
  const registrationDateTime = String(row?.registration_date || "").replace("T", " ").slice(0, 19);
  const closingDate = String(row?.registration_end_date || "").slice(0, 10);
  const lateClosingDate = String(row?.late_fee_upto_date || "").slice(0, 10);
  const closingTime = String(row?.registration_end_time || "23:59:59").slice(0, 8) || "23:59:59";
  const hasDeadlineData = Boolean(registrationDateTime && closingDate && lateClosingDate);
  const isLateRegistration = hasDeadlineData
    && registrationDateTime > `${closingDate} ${closingTime}`
    && registrationDateTime <= `${lateClosingDate} ${closingTime}`;
  const calculatedLateFee = isLateRegistration
    ? Number(row?.configured_late_fee || 0) * (1 + escortCount)
    : 0;
  const lateFee = isFeeExempt
    ? 0
    : (hasDeadlineData ? calculatedLateFee : Number(row?.late_fee_amount || 0));
  const escortFeePerPerson = isFeeExempt ? 0 : Number(row?.escort_fee || 0);
  const totalEscortFee = escortFeePerPerson * escortCount;

  return {
    isFeeExempt,
    escortCount,
    registrationFee,
    lateFee,
    escortFeePerPerson,
    totalEscortFee,
    totalAmountPayable: registrationFee + lateFee + totalEscortFee,
  };
};

const EventRegistrations = () => {
  const { role } = useContext(AuthContext);
  const roleLower = String(role || "").toLowerCase();
  const [selectedGameReg, setSelectedGameReg] = useState(null);
  const [availableGames, setAvailableGames] = useState([]); // games dropdown for the sport
  const [newGameForReg, setNewGameForReg] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState([]);
  const [filter, setFilter] = useState("All");
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [selectedEscort, setSelectedEscort] = useState(null); // 🚀 new
  const [previewImage, setPreviewImage] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventLevelFilter, setEventLevelFilter] = useState(roleLower === "district" ? "district" : "state");
  const [eventYearFilter, setEventYearFilter] = useState("all");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedDistrictFilter, setSelectedDistrictFilter] = useState("all");

  const [regEventLevel, setRegEventLevel] = useState(roleLower === "district" ? "district" : "state");
  const [regEventYear, setRegEventYear] = useState("2026");
  const [regDistrictId, setRegDistrictId] = useState("all");
  const [regCorrespondingDistrictEventId, setRegCorrespondingDistrictEventId] = useState("");
  const [districtEventResults, setDistrictEventResults] = useState([]);
  const [registeredPlayersInDistrictEvent, setRegisteredPlayersInDistrictEvent] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [sendingVenueDetails, setSendingVenueDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");




  useEffect(() => {
    if (eventLevelFilter !== "district") {
      setSelectedDistrictFilter("all");
    }
  }, [eventLevelFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedSearch(searchTerm.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const [allPlayers, setAllPlayers] = useState([]);
  const [eligibleSelectionPlayers, setEligibleSelectionPlayers] = useState([]);
  const [eligiblePlayersLoading, setEligiblePlayersLoading] = useState(false);
  const [selectionBusy, setSelectionBusy] = useState(false);
  const [sendSelectionEmails, setSendSelectionEmails] = useState(true);
  const [selectionForm, setSelectionForm] = useState({
    event_id: "",
    player_id: "",
    game_ids: [],
    payment_proof: null,
    escort_name_1: "",
    escort_email_1: "",
    escort_type_1: "",
    escort_photo_1: null,
    escort_name_2: "",
    escort_email_2: "",
    escort_type_2: "",
    escort_photo_2: null,
  });
  const [selectionGames, setSelectionGames] = useState([]);
  const [eventStatusFilter, setEventStatusFilter] = useState({
    Open: true,
    Closed: true,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bankDetails, setBankDetails] = useState({
    account_name: "Para Sports Association of Andhra Pradesh",
    bank_name: "State Bank of India (SBI)",
    account_number: "41029384756",
    ifsc_code: "SBIN0001234",
    branch: "Vijayawada Main Branch",
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [escortForm, setEscortForm] = useState({
    escort_name: "",
    escort_email: "",
    escort_type: "",
    escort_photo: null,

    escort_name_2: "",
    escort_email_2: "",
    escort_type_2: "",
    escort_photo_2: null,
  });

  useEffect(() => {
    setSelectionForm((prev) => ({ ...prev, event_id: "", game_ids: [] }));
    setRegCorrespondingDistrictEventId("");
    setDistrictEventResults([]);
    setRegisteredPlayersInDistrictEvent([]);
  }, [regEventLevel, regEventYear, regDistrictId]);

  useEffect(() => {
    setRegCorrespondingDistrictEventId("");
    setDistrictEventResults([]);
    setRegisteredPlayersInDistrictEvent([]);
  }, [selectionForm.event_id]);

  const activeDistrictEventId = regEventLevel === "district" ? selectionForm.event_id : regCorrespondingDistrictEventId;

  useEffect(() => {
    let alive = true;
    if (!activeDistrictEventId) {
      setDistrictEventResults([]);
      return;
    }
    const loadResults = async () => {
      try {
        const res = await API.fetchEventResults(activeDistrictEventId);
        if (!alive) return;
        setDistrictEventResults(res || []);
      } catch (err) {
        console.error("Error loading event results:", err);
        if (alive) setDistrictEventResults([]);
      }
    };
    loadResults();
    return () => { alive = false; };
  }, [activeDistrictEventId]);

  useEffect(() => {
    let alive = true;
    if (!regCorrespondingDistrictEventId) {
      setRegisteredPlayersInDistrictEvent([]);
      return;
    }
    const loadRegPlayers = async () => {
      try {
        const res = await API.fetchRegisteredPlayers(regCorrespondingDistrictEventId);
        if (!alive) return;
        setRegisteredPlayersInDistrictEvent(res || []);
      } catch (err) {
        console.error("Error loading registered players for district event:", err);
        if (alive) setRegisteredPlayersInDistrictEvent([]);
      }
    };
    loadRegPlayers();
    return () => { alive = false; };
  }, [regCorrespondingDistrictEventId]);

  const handleDownloadIdCard = async (regId, playerId, eventId) => {
    setDownloadingIds((prev) => {
      const next = new Set(prev);
      next.add(regId);
      return next;
    });
    try {
      await API.downloadAdminSingleId(playerId, eventId);
    } catch (err) {
      console.error("Error downloading ID card:", err);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(regId);
        return next;
      });
    }
  };



  const statusLabel = (row) => {
    if (row.status !== "Pending") return row.status;
    const stage = (row.approval_stage || "District").toString().trim();
    const eventLvl = String(row.event_level || "").toLowerCase();
    const isHigherLvl = ["state", "national", "international"].includes(eventLvl);

    if (stage === "State" && isHigherLvl) {
      return "Forwarded from District";
    }
    if (stage === "District" && isHigherLvl) {
      return "Pending District Forward";
    }
    return `Pending at ${stage}`;
  };

  const statusBadgeClass = (row) => {
    if (row.status === "Approved") return "bg-green-100 text-green-700 border border-green-200";
    if (row.status === "Rejected") return "bg-red-100 text-red-600 border border-red-200";

    const stage = (row.approval_stage || "District").toString().trim();
    const eventLvl = String(row.event_level || "").toLowerCase();
    const isHigherLvl = ["state", "national", "international"].includes(eventLvl);

    if (stage === "State" && isHigherLvl) {
      return "bg-purple-100 text-purple-700 border border-purple-200";
    }
    if (stage === "District" && isHigherLvl) {
      return "bg-orange-100 text-orange-700 border border-orange-200";
    }

    return "bg-yellow-100 text-yellow-700 border border-yellow-200";
  };

  const loadEvents = async () => {
    try {
      const res = await API.fetchAdminEvents({ include_higher: true });
      const list = Array.isArray(res?.data)
        ? res.data
        : (Array.isArray(res) ? res : []);
      setEvents(list);
    } catch (err) {
      console.error("Error loading events:", err);
      setEvents([]);
    }
  };

  const loadPlayers = async () => {
    try {
      const res = await API.fetchAllPlayersFull();
      const list = res?.data || res || [];
      setAllPlayers(Array.isArray(list) ? list : []);
    } catch (_err) {
      setAllPlayers([]);
    }
  };

  const getImageUrl = (path) =>
    resolveAssetUrl(path);

  const loadDistricts = async () => {
    if (roleLower !== "district") {
      try {
        const res = await API.fetchDistrictOptions();
        setDistrictOptions(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Failed to load district options:", err);
      }
    }
  };

  const [globalSummary, setGlobalSummary] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  const loadData = useCallback(async () => {
    setSelectedIds([]);
    try {
      const params = {
        event_level: eventLevelFilter,
        year: eventYearFilter,
        district_id: selectedDistrictFilter,
        status: filter,
        event_id: selectedEvent,
        search: appliedSearch,
        page,
        per_page: limit,
      };

      const [regsRes, statsRes] = await Promise.all([
        API.fetchEventRegistrations(params),
        API.get("/admin/registrations/stats", { params }).catch(() => API.get("/admin/event-registrations/stats", { params })),
      ]);

      const regsData = regsRes?.data || regsRes || [];
      setRegistrations(Array.isArray(regsData) ? regsData : []);
      setTotalCount(regsRes?.total || (Array.isArray(regsData) ? regsData.length : 0));
      setStats(statsRes?.data || statsRes || []);

      if (statsRes?.summary) {
        setGlobalSummary(statsRes.summary);
      } else {
        setGlobalSummary({
          total: regsRes?.total || (Array.isArray(regsData) ? regsData.length : 0),
          pending: 0,
          approved: 0,
          rejected: 0,
        });
      }
    } catch (err) {
      console.error("Error loading registration data:", err);
      errorAlert("Load failed", "Failed to load registration data.");
    }
  }, [eventLevelFilter, eventYearFilter, selectedDistrictFilter, filter, selectedEvent, appliedSearch, page, limit]);

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const baseParams = {
        event_level: eventLevelFilter,
        year: eventYearFilter,
        district_id: selectedDistrictFilter,
        status: filter,
        event_id: selectedEvent,
        search: appliedSearch,
        per_page: 500,
      };
      const allRows = [];
      let exportPage = 1;
      let lastPage = 1;

      do {
        const response = await API.fetchEventRegistrations({ ...baseParams, page: exportPage });
        const pageRows = Array.isArray(response?.data)
          ? response.data
          : (Array.isArray(response) ? response : []);
        allRows.push(...pageRows);
        lastPage = Math.max(Number(response?.last_page) || 1, 1);
        exportPage += 1;
      } while (exportPage <= lastPage);

      if (allRows.length === 0) {
        warningAlert("No records", "There are no event registrations matching the current filters.");
        return;
      }

      const exportRows = allRows.map((row, index) => {
        const {
          isFeeExempt,
          escortCount,
          registrationFee,
          lateFee,
          escortFeePerPerson,
          totalEscortFee,
          totalAmountPayable,
        } = registrationFeeBreakdown(row);

        return {
        serial_no: index + 1,
        registration_id: row.player_registration_id || "",
        player_name: row.player_name || "",
        father_name: row.father_name || "",
        email: row.player_email || "",
        mobile: row.player_mobile || "",
        district: row.player_district || row.district || "",
        event: row.event_title || "",
        event_level: row.event_level || "",
        sport: row.sport_name || "",
        games: parseGames(row).map((game) => game.game_name).filter(Boolean).join(", "),
        registered_by: row.registered_by || "",
        registration_date: row.registration_date || "",
        updated_at: row.updated_at || "",
        status: statusLabel(row),
        fee_exempt: isFeeExempt ? "Yes" : "No",
        registration_fee: registrationFee,
        late_fee: lateFee,
        escort_count: escortCount,
        escort_fee_per_person: escortFeePerPerson,
        total_escort_fee: totalEscortFee,
        total_amount_payable: totalAmountPayable,
        escort_1: row.escort_name || "",
        escort_1_type: row.escort_type || "",
        escort_2: row.escort_name_2 || "",
        escort_2_type: row.escort_type_2 || "",
        };
      });

      await exportToExcel(exportRows, `Event_Registrations_${new Date().toISOString().slice(0, 10)}`, [
        { header: "S.No.", accessor: "serial_no", width: 10 },
        { header: "Player Registration ID", accessor: "registration_id", width: 24 },
        { header: "Player", accessor: "player_name", width: 28 },
        { header: "Father Name", accessor: "father_name", width: 28 },
        { header: "Email", accessor: "email", width: 30 },
        { header: "Mobile", accessor: "mobile", width: 18 },
        { header: "District", accessor: "district", width: 22 },
        { header: "Event", accessor: "event", width: 32 },
        { header: "Level", accessor: "event_level", width: 14 },
        { header: "Sport", accessor: "sport", width: 20 },
        { header: "Games", accessor: "games", width: 35 },
        { header: "Registered By", accessor: "registered_by", width: 18 },
        { header: "Registration Date", accessor: "registration_date", width: 22 },
        { header: "Updated Date", accessor: "updated_at", width: 22 },
        { header: "Status", accessor: "status", width: 24 },
        { header: "Fee Exempt", accessor: "fee_exempt", width: 14 },
        { header: "Registration Fee (₹)", accessor: "registration_fee", width: 20 },
        { header: "Late Fee (₹)", accessor: "late_fee", width: 16 },
        { header: "No. of Escorts", accessor: "escort_count", width: 16 },
        { header: "Escort Fee Each (₹)", accessor: "escort_fee_per_person", width: 20 },
        { header: "Total Escort Fee (₹)", accessor: "total_escort_fee", width: 20 },
        { header: "Total Amount Payable (₹)", accessor: "total_amount_payable", width: 24 },
        { header: "Escort 1", accessor: "escort_1", width: 24 },
        { header: "Escort 1 Type", accessor: "escort_1_type", width: 18 },
        { header: "Escort 2", accessor: "escort_2", width: 24 },
        { header: "Escort 2 Type", accessor: "escort_2_type", width: 18 },
      ]);
    } catch (err) {
      console.error("Event registration Excel export failed:", err);
      errorAlert("Export failed", err?.message || "Failed to export event registrations.");
    } finally {
      setExportingExcel(false);
    }
  };

  useEffect(() => {
    loadEvents();
    loadPlayers();
    loadDistricts();

    API.fetchPublicBankSettings()
      .then((data) => {
        if (data && data.account_name) {
          setBankDetails(data);
        }
      })
      .catch((err) => console.error("Error loading bank settings:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData]);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && setSelectedEscort(null);
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    setSelectedEvent("");
  }, [eventLevelFilter]);

  const filteredRegs = useMemo(() => {
    return registrations.filter((r) => {
      if (!eventStatusFilter[r.event_registration_status || r.registration || "Open"]) {
        return false;
      }
      return true;
    });
  }, [registrations, eventStatusFilter]);

  const filteredStats = useMemo(() => {
    return (Array.isArray(stats) ? stats : []).filter((s) => {
      const ev = (Array.isArray(events) ? events : []).find(
        (e) => String(e.id) === String(s.event_id)
      );
      const lvl = String(ev?.event_level || s.event_level || "district").toLowerCase();

      if (eventLevelFilter === "state") {
        if (lvl === "district") return false;
      } else if (eventLevelFilter === "district") {
        if (lvl !== "district") return false;
        if (selectedDistrictFilter !== "all" && ev && String(ev.district_id) !== String(selectedDistrictFilter)) return false;
      }

      if (eventYearFilter !== "all") {
        const evYear = ev?.event_date ? new Date(ev.event_date).getFullYear() : (ev?.date ? new Date(ev.date).getFullYear() : null);
        if (String(evYear) !== String(eventYearFilter)) return false;
      }

      if (filter !== "All") {
        const val = String(filter).toLowerCase();
        if (val === "pending" && (s.pending || 0) === 0) return false;
        if (val === "approved" && (s.approved || 0) === 0) return false;
        if (val === "rejected" && (s.rejected || 0) === 0) return false;
      }

      return true;
    });
  }, [stats, events, eventLevelFilter, eventYearFilter, selectedDistrictFilter, filter]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedRegs = useMemo(() => {
    if (!sortConfig.key) return filteredRegs;
    return [...filteredRegs].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      if (sortConfig.key === "game_name") {
        aVal = parseGames(a).map((g) => g.game_name).join(", ");
        bVal = parseGames(b).map((g) => g.game_name).join(", ");
      } else if (sortConfig.key === "total_amount_payable") {
        aVal = registrationFeeBreakdown(a).totalAmountPayable;
        bVal = registrationFeeBreakdown(b).totalAmountPayable;
      }
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredRegs, sortConfig]);

  // Reset pagination when search filter or limit changes
  useEffect(() => {
    setPage(1);
  }, [filter, selectedEvent, eventStatusFilter, eventLevelFilter, eventYearFilter, selectedDistrictFilter, appliedSearch, limit]);

  const paginatedRegs = sortedRegs;

  const totalPages = Math.ceil(totalCount / limit) || 1;

  const isAllSelected = useMemo(() => {
    if (filteredRegs.length === 0) return false;
    return filteredRegs.every((r) => selectedIds.includes(r.registration_id));
  }, [filteredRegs, selectedIds]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const filteredRowIds = filteredRegs.map((r) => r.registration_id);
      setSelectedIds((prev) => prev.filter((id) => !filteredRowIds.includes(id)));
    } else {
      const filteredRowIds = filteredRegs.map((r) => r.registration_id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredRowIds])));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (!selectedIds.length) {
      warningAlert("No items selected", "Please select at least one registration first.");
      return;
    }
    const isDistrictForward = roleLower === "district" && newStatus === "Pending";
    const confirm = await Swal.fire({
      title: isDistrictForward
        ? `Forward ${selectedIds.length} registration(s) to State?`
        : `Change Status to ${newStatus} for ${selectedIds.length} registration(s)?`,
      text: isDistrictForward
        ? "These registrations will be forwarded to State level for approval."
        : "Are you sure you want to proceed?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isDistrictForward ? "Forward to State" : "Confirm",
    });
    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: isDistrictForward ? "Forwarding..." : "Updating...",
      text: isDistrictForward
        ? `Forwarding ${selectedIds.length} registration(s)...`
        : `Processing ${selectedIds.length} registration(s)...`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const res = await API.updateRegistrationStatus(id, newStatus);
        completed++;
        Swal.update({
          text: isDistrictForward
            ? `Forwarded ${completed} of ${selectedIds.length} registrations to State...`
            : `Updated ${completed} of ${selectedIds.length} registrations to ${newStatus.toLowerCase()}...`
        });
        return res;
      });
      Swal.close();
      Swal.fire({
        icon: "success",
        title: isDistrictForward ? "Forwarded" : "Updated",
        text: isDistrictForward
          ? "Registrations forwarded to State successfully."
          : `Registrations updated to ${newStatus.toLowerCase()} successfully.`,
        timer: 1400,
        showConfirmButton: false,
      });
      setSelectedIds([]);
      await loadData();
    } catch (err) {
      console.error("Error bulk updating status:", err);
      const errorMsg = err?.message || "Error updating registration status.";
      Swal.close();
      Swal.fire({
        icon: "error",
        title: isDistrictForward ? "Forward failed" : "Update failed",
        text: errorMsg,
      });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    if (!id || updatingStatusId === id) return;
    setUpdatingStatusId(id);
    const isDistrictForward = roleLower === "district" && newStatus === "Pending";
    Swal.fire({
      title: isDistrictForward ? "Forwarding..." : "Updating...",
      text: isDistrictForward ? "Forwarding registration to State..." : `Setting registration to ${newStatus}`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      await API.updateRegistrationStatus(id, newStatus);
      Swal.close();
      Swal.fire({
        icon: "success",
        title: isDistrictForward ? "Forwarded" : "Updated",
        text: isDistrictForward ? "Registration forwarded to State successfully." : `Registration ${newStatus.toLowerCase()} successfully.`,
        timer: 1400,
        showConfirmButton: false,
      });
      await loadData();
    } catch (err) {
      console.error("Error updating status:", err);
      const errorMsg = err?.message || "Error updating registration status.";
      Swal.close();
      Swal.fire({
        icon: "error",
        title: isDistrictForward ? "Forward failed" : "Update failed",
        text: errorMsg,
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openGameChangeFor = async (gameObj, parentRow) => {
    // gameObj: { reg_id, game_id, game_name }  — from games_json parsed
    // parentRow: the r (player-event row) — we need sport_id to load all games for sport
    setSelectedGameReg({
      reg_id: gameObj.reg_id,
      current_game_id: Number(gameObj.game_id),
      current_game_name: gameLabel(gameObj),
      player_name: parentRow.player_name,
      sport_id: parentRow.sport_id,
      event_id: parentRow.event_id,
    });
    setNewGameForReg(String(gameObj.game_id || ""));
    // Prefer event-scoped games; fallback to sport-scoped list.
    let list = [];
    try {
      if (parentRow?.event_id) {
        const eventGamesRes = await API.get(`/admin/game-engine/events/${parentRow.event_id}/games`);
        list = Array.isArray(eventGamesRes?.data)
          ? eventGamesRes.data
          : (Array.isArray(eventGamesRes) ? eventGamesRes : []);
      }
      if (!Array.isArray(list) || !list.length) {
        const res = await API.fetchGamesBySport(parentRow.sport_id);
        list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      }
    } catch (_err) {
      try {
        const res = await API.fetchGamesBySport(parentRow.sport_id);
        list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      } catch (_fallbackErr) {
        list = [];
      }
    }

    setAvailableGames(normalizeGameOptions(list));
  };

  const handleRemoveGame = async (gameObj, parentRow) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Remove selected game?",
      text: `${gameObj.game_name} will be removed from ${parentRow.player_name}'s event registration.`,
      showCancelButton: true,
      confirmButtonText: "Remove Game",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    try {
      await API.removeRegistrationGame(gameObj.reg_id);
      successAlert("Game removed", "The selected game was removed from this event registration.");
      await loadData();
    } catch (err) {
      errorAlert("Unable to remove game", err?.message || "The selected game could not be removed.");
    }
  };

  const openAddGameFor = async (parentRow) => {
    const existingIds = parseGames(parentRow).map((game) => Number(game.game_id)).filter(Boolean);
    setSelectedGameReg({
      mode: "add",
      registration_id: Number(parentRow.registration_id),
      player_name: parentRow.player_name,
      sport_id: parentRow.sport_id,
      event_id: parentRow.event_id,
      existing_game_ids: existingIds,
      max_games: Number(parentRow.max_games_per_player || 3),
    });
    setNewGameForReg("");
    try {
      let list = [];
      if (parentRow?.event_id) {
        const result = await API.get(`/admin/game-engine/events/${parentRow.event_id}/games`);
        list = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
      }
      if (!list.length) {
        const result = await API.fetchGamesBySport(parentRow.sport_id);
        list = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
      }
      setAvailableGames(normalizeGameOptions(list).filter((game) => !existingIds.includes(Number(game.id))));
    } catch (_err) {
      setAvailableGames([]);
    }
  };

  const saveChangedSingleGame = async () => {
    if (!selectedGameReg) return;
    if (!newGameForReg) {
      warningAlert("Game required", "Please select a game.");
      return;
    }
    try {
      if (selectedGameReg.mode === "add") {
        await API.addRegistrationGame(selectedGameReg.registration_id, Number(newGameForReg));
        successAlert("Game added", "The additional game was added successfully.");
      } else {
        await API.changeRegistrationGame(selectedGameReg.reg_id, Number(newGameForReg));
        successAlert("Game updated", "Game changed successfully.");
      }
      setSelectedGameReg(null);
      setAvailableGames([]);
      setNewGameForReg("");
      await loadData(); // reload registrations + stats
    } catch (err) {
      console.error("Error changing game:", err);
      errorAlert("Update failed", err?.message || "Error updating game.");
    }
  };
  // 🚀 Open escort modal
  const openEscortModal = (reg) => {
    setSelectedEscort(reg);

    setEscortForm({
      escort_name: reg.escort_name || "",
      escort_email: reg.escort_email || "",
      escort_type: reg.escort_type || "",
      escort_photo: null,

      escort_name_2: reg.escort_name_2 || "",
      escort_email_2: reg.escort_email_2 || "",
      escort_type_2: reg.escort_type_2 || "",
      escort_photo_2: null,
    });
  };

  // 🚀 Update escort details
  const handleEscortChange = (e) => {
    const { name, value, files } = e.target;
    setEscortForm({
      ...escortForm,
      [name]: files ? files[0] : value,
    });
  };

  const saveEscortDetails = async () => {
    if (!selectedEscort) return;

    try {
      const formData = new FormData();
      formData.append("_method", "PUT");

      formData.append("escort_name", escortForm.escort_name);
      formData.append("escort_email", escortForm.escort_email || "");
      formData.append("escort_type", escortForm.escort_type);
      if (escortForm.escort_photo) {
        formData.append("escort_photo", escortForm.escort_photo);
      }

      // second escort (optional)
      if (escortForm.escort_name_2) {
        formData.append("escort_name_2", escortForm.escort_name_2);
        formData.append("escort_email_2", escortForm.escort_email_2 || "");
        formData.append("escort_type_2", escortForm.escort_type_2);
      }

      if (escortForm.escort_photo_2) {
        formData.append("escort_photo_2", escortForm.escort_photo_2);
      }

      await API.post(
        `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
        formData
      );

      successAlert("Escort updated", "Escort details updated successfully.");
      setSelectedEscort(null);
      await loadData();
    } catch (err) {
      console.error("Error updating escort:", err.response?.data || err.message);
      errorAlert("Update failed", "Error updating escort details.");
    }
  };



  const selectedSelectionEvent = events.find((ev) => String(ev.id) === String(selectionForm.event_id)) || null;
  const selectedSelectionPlayer = [...allPlayers, ...eligibleSelectionPlayers].find((p) => String(p.id) === String(selectionForm.player_id)) || null;
  const selectionEligibility = selectionGames.find((g) => g?.eligibility)?.eligibility || null;
  const registeredPlayerIdsForSelectionEvent = useMemo(
    () =>
      new Set(
        registrations
          .filter((r) => String(r.event_id) === String(selectionForm.event_id))
          .map((r) => String(r.player_id))
      ),
    [registrations, selectionForm.event_id]
  );
  const availableSelectionPlayers = useMemo(() => {
    if (!selectionForm.event_id) return allPlayers;

    let list = eligibleSelectionPlayers;

    // If admin/secretary, filter player list by the selected district
    if (roleLower !== "district") {
      const targetDistId = regDistrictId;
      if (targetDistId && targetDistId !== "all") {
        list = list.filter((p) => 
          String(p.district_id) === String(targetDistId) || 
          String(p.district).toLowerCase() === String(districtOptions.find(d => String(d.id) === String(targetDistId))?.name).toLowerCase()
        );
      }
    }

    // If state event level and corresponding district event is selected, show players registered in that district event
    if (regEventLevel === "state" && regCorrespondingDistrictEventId) {
      const registeredPlayerIdsInDistEvent = new Set(
        registeredPlayersInDistrictEvent.map((p) => String(p.id))
      );
      list = list.filter((p) => registeredPlayerIdsInDistEvent.has(String(p.id)));
    }

    return list.filter((p) => !registeredPlayerIdsForSelectionEvent.has(String(p.id)));
  }, [eligibleSelectionPlayers, regDistrictId, regCorrespondingDistrictEventId, regEventLevel, registeredPlayersInDistrictEvent, registeredPlayerIdsForSelectionEvent, roleLower, allPlayers, selectionForm.event_id, districtOptions]);

  const selectionEscortCount =
    (String(selectionForm.escort_name_1 || "").trim() ? 1 : 0) +
    (String(selectionForm.escort_name_2 || "").trim() ? 1 : 0);
  const selectionFeeExempt = Boolean(selectedSelectionPlayer?.is_fee_exempt);
  const selectionRegistrationFee = selectionFeeExempt ? 0 : Number(selectedSelectionEvent?.registration_fee || 0);
  const selectionEscortFee = selectionFeeExempt ? 0 : Number(selectedSelectionEvent?.escort_fee || 0);
  const selectionTotalPayable = selectionRegistrationFee + selectionEscortFee * selectionEscortCount;

  const getYearFromEvent = (ev) => {
    const dateStr = ev?.event_date || ev?.date || ev?.start_date;
    if (!dateStr) return null;
    const parts = String(dateStr).split("-");
    if (parts.length > 0 && parts[0].length === 4) {
      return parts[0];
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : String(parsed.getFullYear());
  };

  const assistedRegistrationEvents = events.filter((ev) => {
    const registration = String(ev.registration || "Open").toLowerCase().trim();
    return registration !== "closed";
  });

  const filteredRegFormEvents = useMemo(() => {
    return assistedRegistrationEvents.filter((ev) => {
      const lvl = String(ev.event_level || "district").toLowerCase();
      const isLvlMatch = regEventLevel === "state"
        ? ["state", "national", "international"].includes(lvl)
        : lvl === "district";
      if (!isLvlMatch) return false;

      if (regEventYear !== "all") {
        const evYear = getYearFromEvent(ev);
        if (String(evYear) !== String(regEventYear)) return false;
      }

      // If the user is admin/secretary, filter by the selected district
      if (regEventLevel === "district" && roleLower !== "district") {
        if (regDistrictId === "all") return true;
        const targetDistId = regDistrictId;
        const targetDistName = districtOptions.find(d => String(d.id) === String(regDistrictId))?.name;

        if (!targetDistId && !targetDistName) return false;

        const isIdMatch = targetDistId && ev.district_id && String(ev.district_id) === String(targetDistId);
        const isNameMatch = targetDistName && ev.district && String(ev.district).toLowerCase() === String(targetDistName).toLowerCase();
        
        if (!isIdMatch && !isNameMatch) return false;
      }

      return true;
    });
  }, [assistedRegistrationEvents, regEventLevel, regEventYear, regDistrictId, roleLower, districtOptions]);

  const correspondingDistrictEvents = useMemo(() => {
    return events.filter((ev) => {
      const lvl = String(ev.event_level || "district").toLowerCase();
      if (lvl !== "district") return false;

      if (regEventYear !== "all") {
        const evYear = getYearFromEvent(ev);
        if (String(evYear) !== String(regEventYear)) return false;
      }

      // If the user is admin/secretary, filter by the selected district
      if (roleLower !== "district") {
        if (regDistrictId === "all") {
          const selectedStateEvent = events.find(e => String(e.id) === String(selectionForm.event_id));
          return !selectedStateEvent || String(ev.sport_id) === String(selectedStateEvent.sport_id);
        }
        const targetDistId = regDistrictId;
        const targetDistName = districtOptions.find(d => String(d.id) === String(regDistrictId))?.name;

        if (!targetDistId && !targetDistName) return false;

        const isIdMatch = targetDistId && ev.district_id && String(ev.district_id) === String(targetDistId);
        const isNameMatch = targetDistName && ev.district && String(ev.district).toLowerCase() === String(targetDistName).toLowerCase();

        if (!isIdMatch && !isNameMatch) return false;
      }

      const selectedStateEvent = events.find(e => String(e.id) === String(selectionForm.event_id));
      if (selectedStateEvent && String(ev.sport_id) !== String(selectedStateEvent.sport_id)) return false;

      return true;
    });
  }, [events, regEventYear, regDistrictId, roleLower, districtOptions, selectionForm.event_id]);

  useEffect(() => {
    let alive = true;

    const loadEligiblePlayers = async () => {
      if (!selectionForm.event_id) {
        setEligibleSelectionPlayers([]);
        return;
      }

      setEligiblePlayersLoading(true);
      try {
        const rows = await API.fetchEligibleRegistrationPlayers(selectionForm.event_id);
        if (!alive) return;
        const list = Array.isArray(rows) ? rows : [];
        setEligibleSelectionPlayers(list);
        setSelectionForm((prev) => {
          if (!prev.player_id) return prev;
          return list.some((p) => String(p.id) === String(prev.player_id))
            ? prev
            : { ...prev, player_id: "", game_ids: [] };
        });
      } catch (_err) {
        if (!alive) return;
        setEligibleSelectionPlayers([]);
        setSelectionForm((prev) => ({ ...prev, player_id: "", game_ids: [] }));
      } finally {
        if (alive) setEligiblePlayersLoading(false);
      }
    };

    loadEligiblePlayers();
    return () => {
      alive = false;
    };
  }, [selectionForm.event_id]);
  const resetSelectionForm = () => {
    setSelectionForm({
      event_id: "",
      player_id: "",
      game_ids: [],
      payment_proof: null,
      escort_name_1: "",
      escort_email_1: "",
      escort_type_1: "",
      escort_photo_1: null,
      escort_name_2: "",
      escort_email_2: "",
      escort_type_2: "",
      escort_photo_2: null,
    });
  };

  const handleSelectionInputChange = (e) => {
    const { name, value, files } = e.target;
    setSelectionForm((prev) => ({
      ...prev,
      [name]: files ? files[0] || null : value,
    }));
  };

  useEffect(() => {
    if (
      selectionForm.event_id &&
      selectionForm.player_id &&
      registeredPlayerIdsForSelectionEvent.has(String(selectionForm.player_id))
    ) {
      setSelectionForm((prev) => ({ ...prev, player_id: "", game_ids: [] }));
      setSelectionGames([]);
      return;
    }

    const loadSelectionGames = async () => {
      if (!selectedSelectionEvent?.sport_id) {
        setSelectionGames([]);
        setSelectionForm((prev) => ({ ...prev, game_ids: [] }));
        return;
      }
      try {
        const res = await API.fetchGamesBySport(
          selectedSelectionEvent.sport_id,
          selectionForm.player_id ? { player_id: Number(selectionForm.player_id) } : {}
        );
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setSelectionGames(normalizeGameOptions(list));
        setSelectionForm((prev) => ({ ...prev, game_ids: [] }));
      } catch (_err) {
        setSelectionGames([]);
        setSelectionForm((prev) => ({ ...prev, game_ids: [] }));
      }
    };

    loadSelectionGames();
  }, [selectedSelectionEvent?.id, selectedSelectionEvent?.sport_id, selectionForm.event_id, selectionForm.player_id, registeredPlayerIdsForSelectionEvent]);

  const toggleSelectionGame = (game) => {
    const gameId = Number(game?.id || 0);
    const eligibility = game?.eligibility || null;
    if (eligibility?.known && !eligibility?.eligible) {
      warningAlert("Not eligible", eligibility?.reason || "Selected player is not eligible for this game.");
      return;
    }
    setSelectionForm((prev) => {
      const sid = String(gameId);
      const exists = prev.game_ids.includes(sid);
      return {
        ...prev,
        game_ids: exists
          ? prev.game_ids.filter((id) => id !== sid)
          : [...prev.game_ids, sid],
      };
    });
  };

  const submitSelection = async () => {
    if (!selectionForm.event_id || !selectionForm.player_id || !selectionForm.game_ids.length) {
      warningAlert("Missing fields", "Choose event, player, and at least one game.");
      return;
    }
    if (selectionTotalPayable > 0 && !selectionForm.payment_proof) {
      warningAlert("Receipt required", "Upload the fee receipt before submitting this paid registration.");
      return;
    }

    try {
      setSelectionBusy(true);
      const formData = new FormData();
      formData.append("event_id", Number(selectionForm.event_id));
      formData.append("player_id", Number(selectionForm.player_id));
      if (roleLower === "admin") {
        formData.append("send_email_notifications", sendSelectionEmails ? "1" : "0");
      }
      selectionForm.game_ids.forEach((id) => formData.append("game_ids[]", Number(id)));
      if (selectionForm.payment_proof) formData.append("payment_proof", selectionForm.payment_proof);
      [1, 2].forEach((slot) => {
        const name = String(selectionForm[`escort_name_${slot}`] || "").trim();
        if (!name) return;
        formData.append(`escort_name_${slot}`, name);
        formData.append(`escort_email_${slot}`, selectionForm[`escort_email_${slot}`] || "");
        formData.append(`escort_type_${slot}`, selectionForm[`escort_type_${slot}`] || "");
        if (selectionForm[`escort_photo_${slot}`]) {
          formData.append(`escort_photo_${slot}`, selectionForm[`escort_photo_${slot}`]);
        }
      });

      await API.selectPlayerForEvent(formData);
      successAlert("Player registered", "Player was registered or forwarded for this event.");
      resetSelectionForm();
      setSelectionGames([]);
      await loadData();
    } catch (err) {
      errorAlert("Selection failed", err?.message || "Unable to select player for event.");
    } finally {
      setSelectionBusy(false);
    }
  };

  const handleSendVenueDetails = async (communicationType = "venue_details") => {
    if (!selectedEvent) {
      warningAlert("Select event", "Select an event before emailing ID cards.");
      return;
    }
    const event = events.find((item) => String(item.id) === String(selectedEvent));
    const hasSelection = selectedIds.length > 0;
    const includeVenue = communicationType === "venue_details";
    const result = await Swal.fire({
      title: includeVenue ? "Send Venue Details & ID Cards" : "Email ID Cards",
      width: 680,
      html: `
        <div style="text-align:left;display:grid;gap:10px">
          ${hasSelection ? `<label style="font-size:13px;font-weight:600">Recipients</label><select id="venue-send-mode" class="swal2-input" style="margin:0;width:100%"><option value="selected">Selected registrations (${selectedIds.length})</option><option value="all">All approved registrations in this event</option></select>` : `<input id="venue-send-mode" type="hidden" value="all"><p style="font-size:13px;color:#475569;margin:0">Recipients: all approved registrations in the selected event.</p>`}
          <label style="font-size:13px;font-weight:600">Email subject</label><input id="venue-subject" class="swal2-input" style="margin:0;width:100%" value="${includeVenue ? "Venue Details and ID Card" : "Your ID Card"} - ${String(event?.title || "Event").replace(/"/g, "&quot;")}">
          <div style="display:${includeVenue ? "contents" : "none"}">
          <label style="font-size:13px;font-weight:600">Venue</label><textarea id="venue-address" class="swal2-textarea" style="margin:0;width:100%;height:70px">${String(event?.location || "")}</textarea>
          <label style="font-size:13px;font-weight:600">Google Maps link</label><input id="venue-maps" class="swal2-input" style="margin:0;width:100%" placeholder="https://maps.google.com/…">
          <label style="font-size:13px;font-weight:600">Instructions</label><textarea id="venue-instructions" class="swal2-textarea" style="margin:0;width:100%;height:95px" placeholder="Reporting time, documents to carry, dress code, contact details…"></textarea>
          </div>
          <label style="font-size:13px;font-weight:600">Additional text (optional)</label><textarea id="venue-additional" class="swal2-textarea" style="margin:0;width:100%;height:75px" placeholder="Any other message"></textarea>
          <p style="font-size:12px;color:#475569;margin:2px 0 0">Each recipient’s ID card PDF will be generated and attached automatically.</p>
        </div>`,
      showCancelButton: true,
      confirmButtonText: "Send Emails",
      showLoaderOnConfirm: true,
      preConfirm: () => {
        const value = (id) => document.getElementById(id)?.value?.trim() || "";
        const payload = {
          event_id: Number(selectedEvent),
          communication_type: communicationType,
          send_mode: value("venue-send-mode"),
          registration_ids: selectedIds.map(Number),
          subject: value("venue-subject"),
          venue: value("venue-address"),
          maps_url: value("venue-maps"),
          instructions: value("venue-instructions"),
          additional_text: value("venue-additional"),
        };
        if (!payload.subject || (includeVenue && !payload.venue)) {
          Swal.showValidationMessage(includeVenue ? "Subject and venue are required." : "Subject is required.");
          return false;
        }
        if (payload.maps_url && !/^https?:\/\//i.test(payload.maps_url)) {
          Swal.showValidationMessage("Enter a complete Google Maps URL beginning with http:// or https://.");
          return false;
        }
        return payload;
      },
    });
    if (!result.isConfirmed || !result.value) return;
    setSendingVenueDetails(true);
    try {
      const response = await API.sendRegistrationVenueDetails(result.value);
      successAlert("Communication completed", response?.message || "ID cards were sent.");
    } catch (err) {
      errorAlert("Send failed", err?.message || "Unable to send venue details.");
    } finally {
      setSendingVenueDetails(false);
    }
  };

  if (!["admin", "secretary", "district"].includes(roleLower)) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Approval Desk</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Event Registrations</h1>
              <p className="text-sm text-slate-600 mt-1">
                Control approvals, escort updates, nominations, and ID-card generation from one board.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <button
                type="button"
                onClick={() => setFilter("All")}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left cursor-pointer hover:bg-slate-50 transition-all shadow-sm"
              >
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Total</p>
                <p className="text-xl font-extrabold text-slate-900">{globalSummary.total || totalCount}</p>
              </button>
              <button
                type="button"
                onClick={() => setFilter("All")}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left cursor-pointer hover:bg-slate-50 transition-all shadow-sm"
              >
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Filtered</p>
                <p className="text-xl font-extrabold text-slate-900">{totalCount}</p>
              </button>
              <button
                type="button"
                onClick={() => setFilter("Pending")}
                className={`rounded-xl border px-3 py-2 text-left cursor-pointer transition-all shadow-sm ${
                  filter === "Pending" ? "bg-amber-100 border-amber-400 ring-2 ring-amber-300" : "bg-amber-50 border-amber-200 hover:bg-amber-100/50"
                }`}
              >
                <p className="text-[11px] uppercase tracking-wide text-amber-700 font-bold">Pending</p>
                <p className="text-xl font-extrabold text-amber-800">{globalSummary.pending || 0}</p>
              </button>
              <button
                type="button"
                onClick={() => setFilter("Approved")}
                className={`rounded-xl border px-3 py-2 text-left cursor-pointer transition-all shadow-sm ${
                  filter === "Approved" ? "bg-emerald-100 border-emerald-400 ring-2 ring-emerald-300" : "bg-emerald-50 border-emerald-200 hover:bg-emerald-100/50"
                }`}
              >
                <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-bold">Approved</p>
                <p className="text-xl font-extrabold text-emerald-800">{globalSummary.approved || 0}</p>
              </button>
              <button
                type="button"
                onClick={() => setFilter("Rejected")}
                className={`rounded-xl border px-3 py-2 text-left cursor-pointer transition-all shadow-sm ${
                  filter === "Rejected" ? "bg-rose-100 border-rose-400 ring-2 ring-rose-300" : "bg-rose-50 border-rose-200 hover:bg-rose-100/50"
                }`}
              >
                <p className="text-[11px] uppercase tracking-wide text-rose-700 font-bold">Rejected</p>
                <p className="text-xl font-extrabold text-rose-800">{globalSummary.rejected || 0}</p>
              </button>
            </div>
          </div>
        </section>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-6">
              {(roleLower === "district" || roleLower === "admin" || roleLower === "secretary") && (
                <div className="flex bg-slate-200/80 p-1 rounded-xl shrink-0">
                  <button
                    type="button"
                    onClick={() => setEventLevelFilter("all")}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    All Levels
                  </button>
                  <button
                    type="button"
                    onClick={() => setEventLevelFilter("state")}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "state" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    State & Above
                  </button>
                  <button
                    type="button"
                    onClick={() => setEventLevelFilter("district")}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "district" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    District Level
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Year:</span>
                <SearchableSelect
                  value={eventYearFilter}
                  onChange={(e) => setEventYearFilter(e.target.value)}
                  className="border p-2 rounded w-36 text-sm font-medium bg-white text-slate-800"
                >
                  <option value="all">All Years</option>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                </SearchableSelect>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Status:</span>
                <SearchableSelect
                  className="border p-2 rounded w-36 text-sm font-medium bg-white text-slate-800"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </SearchableSelect>
              </div>

              {eventLevelFilter === "district" && roleLower !== "district" && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-700">District:</span>
                  <SearchableSelect
                    value={selectedDistrictFilter}
                    onChange={(e) => setSelectedDistrictFilter(e.target.value)}
                    className="border p-2 rounded w-48 text-sm font-medium bg-white text-slate-800"
                  >
                    <option value="all">All Districts</option>
                    {(Array.isArray(districtOptions) ? districtOptions : []).map((dist) => (
                      <option key={dist.id} value={dist.id}>
                        {dist.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 hidden sm:block">
              Showing stats and registrations for the selected filters.
            </div>
          </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {filteredStats.map((s) => (
            <div key={s.event_id} className="p-3 sm:p-4 border border-slate-200 rounded-2xl bg-white shadow-sm">
              <h3 
                onClick={() => setSelectedEvent(s.event_id)} 
                className="font-semibold text-blue-700 text-base sm:text-lg mb-2 cursor-pointer hover:underline"
              >
                {s.event_title}
              </h3>
              <p 
                onClick={() => { setSelectedEvent(s.event_id); setFilter("Pending"); }} 
                className="cursor-pointer hover:text-amber-600 transition-all"
              >
                Pending: {s.pending || 0}
              </p>
              <p 
                onClick={() => { setSelectedEvent(s.event_id); setFilter("Approved"); }} 
                className="text-green-700 cursor-pointer hover:text-green-800 transition-all"
              >
                Approved: {s.approved || 0}
              </p>
              <p 
                onClick={() => { setSelectedEvent(s.event_id); setFilter("Rejected"); }} 
                className="text-red-600 cursor-pointer hover:text-red-700 transition-all"
              >
                Rejected: {s.rejected || 0}
              </p>
            </div>
          ))}
        </div>



        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-4">
          <h3 className="text-base font-semibold text-indigo-700 mb-2">
            Register Player for Event
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            District users can register their players. State/Admin registrations are approved immediately; district registrations for higher-level events go to state approval.
          </p>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Event Level</label>
              <div className="flex bg-slate-200/80 p-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => setRegEventLevel("district")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${regEventLevel === "district" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  District Event
                </button>
                <button
                  type="button"
                  onClick={() => setRegEventLevel("state")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${regEventLevel === "state" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  State Event
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Year</label>
              <SearchableSelect
                value={regEventYear}
                onChange={(e) => setRegEventYear(e.target.value)}
                className="w-full border p-2 rounded text-sm bg-white"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </SearchableSelect>
            </div>

            {roleLower !== "district" && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">District</label>
                <SearchableSelect
                  value={regDistrictId}
                  onChange={(e) => setRegDistrictId(e.target.value)}
                  className="w-full border p-2 rounded text-sm bg-white"
                >
                  <option value="all">All Districts</option>
                  {(Array.isArray(districtOptions) ? districtOptions : []).map((dist) => (
                    <option key={dist.id} value={dist.id}>
                      {dist.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Event</label>
              <SearchableSelect
                value={selectionForm.event_id}
                onChange={(e) =>
                  setSelectionForm((prev) => ({
                    ...prev,
                    event_id: e.target.value,
                    game_ids: [],
                  }))
                }
                className="w-full border p-2 rounded"
                disabled={regEventLevel === "district" && roleLower !== "district" && !regDistrictId}
              >
                <option value="">-- Select Event --</option>
                {filteredRegFormEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title} {ev.event_level ? `(${String(ev.event_level).toUpperCase()})` : ""}
                  </option>
                ))}
              </SearchableSelect>

              {regEventLevel === "state" && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">
                    Corresponding District Event <span className="text-xs text-slate-500 font-normal">(Optional)</span>
                  </label>
                  <SearchableSelect
                    value={regCorrespondingDistrictEventId}
                    onChange={(e) => setRegCorrespondingDistrictEventId(e.target.value)}
                    className="w-full border p-2 rounded"
                    disabled={!selectionForm.event_id || (roleLower !== "district" && !regDistrictId)}
                  >
                    <option value="">-- Select Corresponding District Event (Optional) --</option>
                    {correspondingDistrictEvents.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title} ({new Date(ev.date).getFullYear()})
                      </option>
                    ))}
                  </SearchableSelect>
                  <p className="text-xs text-slate-500 mt-1">
                    If selected, only players registered in that district event will be shown. Otherwise, all district players are shown.
                  </p>
                </div>
              )}

              {districtEventResults && districtEventResults.length > 0 && (
                <div className="mt-4 p-4 border border-blue-200 bg-blue-50 rounded-xl">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                    🏆 Top Positions in District Event
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs text-left text-slate-700">
                      <thead>
                        <tr className="border-b border-blue-200 text-blue-900 font-semibold">
                          <th className="pb-1.5 pr-4">Game</th>
                          <th className="pb-1.5 pr-4">Player</th>
                          <th className="pb-1.5 pr-4">Age</th>
                          <th className="pb-1.5">Position</th>
                        </tr>
                      </thead>
                      <tbody>
                        {districtEventResults.map((r, idx) => (
                          <tr key={idx} className="border-b border-blue-100 last:border-0 hover:bg-blue-100/50">
                            <td className="py-1.5 pr-4 font-medium">{r.game_name}</td>
                            <td className="py-1.5 pr-4">{r.player_name}</td>
                            <td className="py-1.5 pr-4">{r.age !== null ? `${r.age} yrs` : "—"}</td>
                            <td className="py-1.5 font-semibold text-blue-950">
                              {r.position === 1 ? "🥇 1st" : r.position === 2 ? "🥈 2nd" : r.position === 3 ? "🥉 3rd" : `${r.position}th`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Player</label>
              <SearchableSelect
                value={selectionForm.player_id}
                onChange={(e) =>
                  setSelectionForm((prev) => ({ ...prev, player_id: e.target.value }))
                }
                className="w-full border p-2 rounded"
                disabled={!selectionForm.event_id || eligiblePlayersLoading}
              >
                <option value="">{eligiblePlayersLoading ? "Loading eligible players..." : "-- Select Player --"}</option>
                {availableSelectionPlayers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.district || "-"})
                  </option>
                ))}
              </SearchableSelect>
              {selectionForm.event_id && !availableSelectionPlayers.length ? (
                <p className="text-xs text-slate-500 mt-1">
                  No players are eligible for this selected event quota, or all eligible players are already registered.
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">Games</label>
            {selectionEligibility ? (
              <p className="text-xs text-gray-600 mb-2">
                Category: {formatAgeCategoryLabel(selectionEligibility.player_age_category)} | Gender:{" "}
                {selectionEligibility.player_gender || "-"} | Class: {selectionEligibility.player_classification || "-"}
              </p>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {selectionGames.map((g) => {
                const checked = selectionForm.game_ids.includes(String(g.id));
                return (
                  <label
                    key={g.id}
                    className={`flex items-start gap-2 border rounded px-2 py-1 text-sm ${
                      g?.eligibility?.known && !g?.eligibility?.eligible ? "bg-rose-50 border-rose-200" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={g?.eligibility?.known && !g?.eligibility?.eligible}
                      onChange={() => toggleSelectionGame(g)}
                    />
                    <span>
                      <span>{g.game_name}</span>
                      {g?.eligibility?.eligible_classes ? (
                        <div className="text-[11px] text-gray-500">
                          Eligible classes: {g.eligibility.eligible_classes}
                        </div>
                      ) : null}
                      {g?.eligibility?.known && !g?.eligibility?.eligible && g?.eligibility?.reason ? (
                        <div className="text-[11px] text-rose-700">{g.eligibility.reason}</div>
                      ) : null}
                    </span>
                  </label>
                );
              })}
              {!selectionGames.length ? (
                <span className="text-xs text-gray-500">Select a higher-level event to load games.</span>
              ) : null}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <label className="block text-sm font-medium mb-1">
                Fee Receipt {selectionTotalPayable > 0 ? "(Required)" : "(Optional)"}
              </label>
              {selectionFeeExempt ? (
                <p className="text-xs text-emerald-700 mb-2">
                  Fee exemption is active for this player.
                </p>
              ) : (
                <p className="text-xs text-slate-600 mb-2">
                  Registration: ₹{selectionRegistrationFee || 0} | Escort: ₹{selectionEscortFee || 0} each | Total: ₹{selectionTotalPayable || 0}
                </p>
              )}

              {selectionTotalPayable > 0 && bankDetails && (
                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-inner space-y-1 text-[11px] text-slate-700 mb-2">
                  <p className="font-bold text-slate-500 uppercase tracking-wider text-[10px] mb-1">Association Bank Account Details</p>
                  <p><strong>Account Name:</strong> {bankDetails.account_name}</p>
                  <p><strong>Bank:</strong> {bankDetails.bank_name}</p>
                  <p><strong>A/C No:</strong> {bankDetails.account_number}</p>
                  <p><strong>IFSC Code:</strong> {bankDetails.ifsc_code}</p>
                  <p><strong>Branch:</strong> {bankDetails.branch}</p>
                </div>
              )}

              <input
                type="file"
                name="payment_proof"
                accept="image/*,.pdf"
                onChange={handleSelectionInputChange}
                className="w-full border border-slate-300 bg-white p-2 rounded text-sm"
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">Escort / Guide Runner 1</h4>
              <input
                type="text"
                name="escort_name_1"
                value={selectionForm.escort_name_1}
                onChange={handleSelectionInputChange}
                placeholder="Name"
                className="w-full border p-2 rounded mb-2 text-sm"
              />
              <input
                type="email"
                name="escort_email_1"
                value={selectionForm.escort_email_1}
                onChange={handleSelectionInputChange}
                placeholder="Email"
                className="w-full border p-2 rounded mb-2 text-sm"
              />
              <input
                type="text"
                name="escort_type_1"
                value={selectionForm.escort_type_1}
                onChange={handleSelectionInputChange}
                placeholder="Guide Runner / Coach / Parent / Helper"
                className="w-full border p-2 rounded mb-2 text-sm"
              />
              <input
                type="file"
                name="escort_photo_1"
                accept="image/*"
                onChange={handleSelectionInputChange}
                className="w-full border p-2 rounded text-sm"
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">Escort / Guide Runner 2</h4>
              <input
                type="text"
                name="escort_name_2"
                value={selectionForm.escort_name_2}
                onChange={handleSelectionInputChange}
                placeholder="Name"
                className="w-full border p-2 rounded mb-2 text-sm"
              />
              <input
                type="email"
                name="escort_email_2"
                value={selectionForm.escort_email_2}
                onChange={handleSelectionInputChange}
                placeholder="Email"
                className="w-full border p-2 rounded mb-2 text-sm"
              />
              <input
                type="text"
                name="escort_type_2"
                value={selectionForm.escort_type_2}
                onChange={handleSelectionInputChange}
                placeholder="Guide Runner / Coach / Parent / Helper"
                className="w-full border p-2 rounded mb-2 text-sm"
              />
              <input
                type="file"
                name="escort_photo_2"
                accept="image/*"
                onChange={handleSelectionInputChange}
                className="w-full border p-2 rounded text-sm"
              />
            </div>
          </div>

          {roleLower === "admin" && (
            <label className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={sendSelectionEmails}
                onChange={(e) => setSendSelectionEmails(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-semibold text-slate-800">Send registration emails</span>
                <span className="block text-xs text-slate-600 mt-0.5">
                  Turn this off to register or forward the player without sending registration email notifications.
                </span>
              </span>
            </label>
          )}

          <div className="mt-5 flex justify-end">
            <button
              onClick={submitSelection}
              disabled={
                selectionBusy ||
                !selectionForm.event_id ||
                !selectionForm.player_id ||
                !selectionForm.game_ids.length ||
                (selectionTotalPayable > 0 && !selectionForm.payment_proof)
              }
              className="px-6 py-2.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-400 font-semibold text-sm shadow-sm transition"
            >
              {selectionBusy ? "Submitting..." : "Select / Forward Player"}
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-4 flex flex-wrap gap-4 items-end">


          {/* Event selector */}
          <div>
            <label className="block text-sm font-medium mb-1">Event</label>
            <SearchableSelect
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="border p-2 rounded w-64"
            >
              <option value="">All Events</option>
              {(Array.isArray(events) ? events : [])
                .filter((ev) => {
                  const lvl = String(ev.event_level || "district").toLowerCase();
                  if (eventLevelFilter === "state") {
                    if (lvl === "district") return false;
                  } else if (eventLevelFilter === "district") {
                    if (lvl !== "district") return false;
                    if (selectedDistrictFilter !== "all" && String(ev.district_id) !== String(selectedDistrictFilter)) return false;
                  }

                  // Filter by status if selected
                  const s = (Array.isArray(stats) ? stats : []).find(
                    (st) => String(st.event_id) === String(ev.id)
                  );
                  if (filter !== "All" && s) {
                    const val = String(filter).toLowerCase();
                    if (val === "pending" && (s.pending || 0) === 0) return false;
                    if (val === "approved" && (s.approved || 0) === 0) return false;
                    if (val === "rejected" && (s.rejected || 0) === 0) return false;
                  }

                  return true;
                })
                .map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title} {ev.event_level ? `(${String(ev.event_level).toUpperCase()})` : ""}
                  </option>
                ))}
            </SearchableSelect>
          </div>

          {/* Open / Closed filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Event Status</label>
            <div className="flex gap-3">
              {["Open", "Closed"].map(s => (
                <label key={s} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={eventStatusFilter[s]}
                    onChange={() =>
                      setEventStatusFilter(prev => ({
                        ...prev,
                        [s]: !prev[s],
                      }))
                    }
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>

          {/* Show items per page */}
          <div>
            <label className="block text-sm font-medium mb-1">Show</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="border p-2 rounded w-24 bg-white"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* ID-card download and email actions */}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            <button
              disabled={!selectedEvent || bulkDownloading}
              onClick={async () => {
                if (!selectedEvent) return;
                const result = await Swal.fire({
                  icon: "question",
                  title: "Download all approved ID cards?",
                  text: "Download ALL approved ID cards for this event?",
                  showCancelButton: true,
                  confirmButtonText: "Download",
                });
                if (!result.isConfirmed) return;
                setBulkDownloading(true);
                try {
                  await API.downloadAdminBulkIds(selectedEvent);
                } catch (err) {
                  console.error("Error downloading bulk ID cards:", err);
                } finally {
                  setBulkDownloading(false);
                }
              }}
              className={`px-4 py-2 rounded text-white flex items-center justify-center gap-1.5 min-w-[125px] transition ${
                selectedEvent && !bulkDownloading
                  ? "bg-indigo-600 hover:bg-indigo-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {bulkDownloading ? (
                <>
                  <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full"></span>
                  Downloading
                </>
              ) : (
                "Bulk ID Cards"
              )}
            </button>
            <button
              type="button"
              onClick={() => handleSendVenueDetails("id_card_only")}
              disabled={!selectedEvent || sendingVenueDetails}
              className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-semibold"
              title="Email automatically generated ID cards without venue details"
            >
              {sendingVenueDetails ? "Sending..." : selectedIds.length > 0 ? "Email ID Cards" : "Email All ID Cards"}
            </button>
            <button
              type="button"
              onClick={() => handleSendVenueDetails("venue_details")}
              disabled={!selectedEvent || sendingVenueDetails}
              className="bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-semibold"
              title="Send venue, Google Maps link, optional instructions and attached ID cards"
            >
              {sendingVenueDetails ? "Sending..." : selectedIds.length > 0 ? "Venue + ID Cards" : "Venue + All ID Cards"}
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-1 w-full">
            <span className="text-sm font-semibold text-slate-800">
              Bulk Actions {selectedIds.length > 0 ? `(${selectedIds.length} selected)` : "(Select checkboxes in the table to use)"}
            </span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-xs text-red-600 hover:underline font-semibold"
              >
                Clear Selection
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleSendVenueDetails("id_card_only")}
              disabled={!selectedEvent || sendingVenueDetails}
              className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white px-3 py-1.5 rounded text-xs font-semibold"
              title="Email automatically generated ID cards without venue details"
            >
              {sendingVenueDetails ? "Sending..." : selectedIds.length > 0 ? "Email ID Cards" : "Email All ID Cards"}
            </button>
            <button
              type="button"
              onClick={() => handleSendVenueDetails("venue_details")}
              disabled={!selectedEvent || sendingVenueDetails}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-3 py-1.5 rounded text-xs font-semibold"
              title="Send venue, Google Maps link, instructions and an automatically generated ID card"
            >
              {sendingVenueDetails ? "Sending…" : selectedIds.length > 0 ? "Send Venue + ID Card" : "Send to All + ID Cards"}
            </button>
            {roleLower !== "district" ? (
              <>
                <button
                  type="button"
                  onClick={() => handleBulkStatusChange("Approved")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                >
                  Approve Selected
                </button>
                <button
                  type="button"
                  onClick={() => handleBulkStatusChange("Rejected")}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                >
                  Reject Selected
                </button>
              </>
            ) : (
              <>
                {/* District user logic: for state/higher-level events, forward to state instead of approve */}
                {(selectedIds.length === 0 ? eventLevelFilter !== "district" : registrations
                  .filter((r) => selectedIds.includes(r.registration_id))
                  .some((r) => ["state", "national", "international"].includes(String(r.event_level || "").toLowerCase()))) && (
                  <button
                    type="button"
                    onClick={() => handleBulkStatusChange("Pending")}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Forward Selected to State
                  </button>
                )}

                {(selectedIds.length === 0 ? eventLevelFilter !== "state" : registrations
                  .filter((r) => selectedIds.includes(r.registration_id))
                  .some((r) => String(r.event_level || "district").toLowerCase() === "district")) && (
                  <button
                    type="button"
                    onClick={() => handleBulkStatusChange("Approved")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Approve Selected
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleBulkStatusChange("Rejected")}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
                >
                  Reject Selected
                </button>
              </>
            )}
          </div>
        </div>

        {/* Table */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Registration Queue</h3>
              <p className="text-sm text-slate-600">
                Review status, escorts, payment proofs, and event game mappings for each player.
              </p>
            </div>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaFileExcel aria-hidden="true" />
              {exportingExcel ? "Exporting..." : "Export Excel"}
            </button>
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setAppliedSearch(searchTerm.trim());
            }}
            className="border-b border-indigo-100 bg-indigo-50/60 px-4 py-3"
          >
            <label htmlFor="registration-queue-search" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-indigo-900">
              Search Registrations
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="registration-queue-search"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Enter player name, registration ID, district, event, sport, game, email or mobile"
                className="min-w-0 flex-1 rounded-lg border border-indigo-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <button type="submit" className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700">
                Search
              </button>
              {(searchTerm || appliedSearch) && (
                <button
                  type="button"
                  onClick={() => { setSearchTerm(""); setAppliedSearch(""); setPage(1); }}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-slate-600">
              {appliedSearch ? `Showing ${totalCount} result(s) for “${appliedSearch}”.` : `Showing ${totalCount} registration(s).`}
            </p>
          </form>
        <div className="overflow-x-auto">
          <table style={{ verticalAlign: "middle" }} className="event-reg-table min-w-full border border-gray-300 rounded-lg text-xs sm:text-sm">
            <thead className="bg-blue-100 text-gray-700 select-none">
              <tr>
                <th className="p-2 border text-center w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="cursor-pointer rounded"
                  />
                </th>
                <th className="p-2 border">S.No.</th>
                {[
                  { label: "Registration ID", key: "player_registration_id" },
                  { label: "Player", key: "player_name" },
                  { label: "Disability Type", key: "disability_types" },
                  { label: "Event", key: "event_title" },
                  { label: "Level", key: "event_level" },
                  { label: "Registered By", key: "registered_by_name" },
                  { label: "Sport", key: "sport_name" },
                  { label: "Game", key: "game_name" },
                  { label: "Escort", key: "escort_name" },
                  { label: "Payment", key: "payment_status" },
                  { label: "Amount Payable", key: "total_amount_payable" },
                  { label: "Status", key: "approval_status" },
                ].map((col, idx) => (
                  <th
                    key={idx}
                    onClick={() => handleSort(col.key)}
                    className="p-2 border cursor-pointer hover:bg-blue-200 transition"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>{col.label}</span>
                      <span className="text-[10px] text-gray-400">
                        {sortConfig.key === col.key
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : "↕"}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRegs.map((r, rowIdx) => {
                let disText = "-";
                if (Array.isArray(r.disability_types)) {
                  disText = r.disability_types.join(", ");
                } else if (typeof r.disability_types === "string" && r.disability_types.trim()) {
                  try {
                    const parsed = JSON.parse(r.disability_types);
                    if (Array.isArray(parsed)) disText = parsed.join(", ");
                    else disText = r.disability_types;
                  } catch (_e) {
                    disText = r.disability_types;
                  }
                }

                const feeBreakdown = registrationFeeBreakdown(r);

                return (
                <tr key={r.registration_id} className="text-center">
                  <td className="p-2 border text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(r.registration_id)}
                      onChange={() => toggleSelectRow(r.registration_id)}
                      className="cursor-pointer rounded"
                    />
                  </td>
                  <td className="p-2 border font-semibold text-slate-800">{ (page - 1) * limit + rowIdx + 1 }</td>
                  <td className="p-2 border font-bold text-blue-900 text-center whitespace-nowrap">{r.player_registration_id || "-"}</td>
                  <td className="p-2 border font-medium text-left">
                    <div>{r.player_name}</div>
                    <div className="text-[11px] font-normal text-slate-500">Father: <span className="font-semibold text-slate-700">{r.father_name || "-"}</span></div>
                    {(r.player_district || r.district) && (
                      <div className="text-[11px] font-normal text-slate-500">
                        District: <span className="font-semibold text-slate-700">{r.player_district || r.district}</span>
                      </div>
                    )}
                  </td>
                  <td className="p-2 border text-xs font-medium text-amber-900 bg-amber-50/50">{disText || "-"}</td>
                  <td className="p-2 border text-left">
                    <div className="font-medium">{r.event_title}</div>
                    <div className="text-[11px] text-slate-500">{r.event_date}</div>
                  </td>
                  <td className="p-2 border uppercase text-xs font-semibold">{r.event_level || "-"}</td>
                  <td className="p-2 border text-xs">{r.registered_by_name || r.registered_by || "-"}</td>
                  <td className="p-2 border text-xs">{r.sport_name}</td>
                  <td className="p-2 border text-xs">
                    {(() => {
                      const gamesList = parseGames(r);
                      if (!gamesList.length) {
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-gray-400 font-medium">-</span>
                            {['admin', 'secretary'].includes(roleLower) && (
                              <button type="button" onClick={() => openAddGameFor(r)} className="text-[10px] text-emerald-700 underline font-semibold">
                                + Add Game (0/{Number(r.max_games_per_player || 3)})
                              </button>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div className="flex flex-col items-center gap-1 font-medium">
                          {gamesList.map((g, gIdx) => (
                            <div key={gIdx} className="flex flex-col items-center gap-0.5 border-b border-slate-100 last:border-b-0 pb-1 last:pb-0">
                              <span className="font-semibold text-slate-800">{g.game_name}</span>
                              {roleLower !== "player" && (
                                <div className="flex items-center gap-2">
                                  <button type="button" onClick={() => openGameChangeFor(g, r)} className="text-[10px] text-indigo-600 hover:text-indigo-800 underline font-medium">Change</button>
                                  {['admin', 'secretary'].includes(roleLower) && Number(g.reg_id) > 0 && (
                                    <button type="button" onClick={() => handleRemoveGame(g, r)} className="text-[10px] text-red-600 hover:text-red-800 underline font-semibold">Delete</button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                          {['admin', 'secretary'].includes(roleLower) && gamesList.length < Number(r.max_games_per_player || 3) && (
                            <button
                              type="button"
                              onClick={() => openAddGameFor(r)}
                              className="mt-1 text-[10px] text-emerald-700 hover:text-emerald-900 underline font-semibold"
                            >
                              + Add Game ({gamesList.length}/{Number(r.max_games_per_player || 3)})
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-2 border text-left align-top">
                    {/* Escort 1 */}
                    {r.escort_name ? (
                      <div className="mb-1">
                        <p className="font-medium text-xs">{r.escort_name}</p>
                        {r.escort_email && <p className="text-[11px] text-slate-500">{r.escort_email}</p>}
                        <p className="text-[11px] text-gray-600">{r.escort_type}</p>

                        {r.escort_photo && (
                          <button
                            onClick={() => setPreviewImage(getImageUrl(r.escort_photo))}
                            className="text-blue-600 text-[11px] underline"
                          >
                            View Photo
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">No Escort</p>
                    )}

                    {/* Escort 2 */}
                    {r.escort_name_2 && (
                      <div className="mt-1 pt-1 border-t">
                        <p className="font-medium text-xs">{r.escort_name_2}</p>
                        {r.escort_email_2 && <p className="text-[11px] text-slate-500">{r.escort_email_2}</p>}
                        <p className="text-[11px] text-gray-600">{r.escort_type_2}</p>

                        {r.escort_photo_2 && (
                          <button
                            onClick={() => setPreviewImage(getImageUrl(r.escort_photo_2))}
                            className="text-blue-600 text-[11px] underline"
                          >
                            View Photo
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2 border">
                    {r.payment_proof ? (
                      <button
                        onClick={() => setPreviewImage(getImageUrl(r.payment_proof))}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        View
                      </button>
                    ) : (
                      "Not Uploaded"
                    )}
                  </td>
                  <td className="p-2 border text-left whitespace-nowrap">
                    {feeBreakdown.isFeeExempt ? (
                      <div>
                        <span className="font-bold text-emerald-700">₹0</span>
                        <div className="text-[10px] text-emerald-600">Fee exempt</div>
                      </div>
                    ) : (
                      <div className="space-y-0.5 text-[10px] text-slate-600">
                        <div>Registration: ₹{feeBreakdown.registrationFee}</div>
                        <div>Escorts: {feeBreakdown.escortCount} × ₹{feeBreakdown.escortFeePerPerson}</div>
                        <div className="border-t border-slate-200 pt-0.5 text-xs font-bold text-slate-900">
                          Total: ₹{feeBreakdown.totalAmountPayable}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="p-2 border">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${statusBadgeClass(r)}`}>
                        {statusLabel(r)}
                      </span>
                      {(r.player_district || r.district) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                          {r.player_district || r.district}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 border">
                    <div className="flex flex-nowrap justify-center items-center gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <button
                        onClick={() => openEscortModal(r)}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded text-[11px] whitespace-nowrap"
                      >
                        Change Escort
                      </button>

                      {/* Approve / Forward button based on role & event level */}
                      {roleLower === "district" && ["state", "national", "international"].includes(String(r.event_level || "").toLowerCase()) ? (
                        r.status === "Approved" ? (
                          <button
                            disabled
                            className="px-2 py-1 rounded text-[11px] whitespace-nowrap bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold cursor-not-allowed"
                            title="Already Approved by State"
                          >
                            ✓ Approved
                          </button>
                        ) : (r.approval_stage || "District").toString().trim() === "State" ? (
                          <button
                            disabled
                            className="px-2 py-1 rounded text-[11px] whitespace-nowrap bg-purple-100 text-purple-800 border border-purple-300 font-bold cursor-not-allowed"
                            title="Already Forwarded to State"
                          >
                            ✓ Forwarded to State
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(r.registration_id, "Pending")}
                            className="px-2 py-1 rounded text-[11px] whitespace-nowrap bg-amber-600 hover:bg-amber-700 text-white shadow-sm font-semibold transition"
                            title="Forward player registration to State"
                          >
                            Forward to State
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => r.status !== "Approved" && handleStatusChange(r.registration_id, "Approved")}
                          disabled={r.status === "Approved"}
                          className={`px-2 py-1 rounded text-[11px] whitespace-nowrap transition font-medium ${
                            r.status === "Approved"
                              ? "bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold cursor-not-allowed"
                              : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                          }`}
                          title={r.status === "Approved" ? "Already Approved" : "Click to Approve"}
                        >
                          {r.status === "Approved" ? "✓ Approved" : "Approve"}
                        </button>
                      )}

                      {/* Separate Reject button */}
                      <button
                        onClick={() => r.status !== "Rejected" && handleStatusChange(r.registration_id, "Rejected")}
                        disabled={
                          r.status === "Rejected" ||
                          (roleLower === "district" &&
                            ["state", "national", "international"].includes(String(r.event_level || "").toLowerCase()) &&
                            ((r.approval_stage || "District").toString().trim() !== "District" || r.status === "Approved"))
                        }
                        className={`px-2 py-1 rounded text-[11px] whitespace-nowrap transition font-medium ${
                          r.status === "Rejected"
                            ? "bg-red-100 text-red-800 border border-red-300 font-bold cursor-not-allowed"
                            : (roleLower === "district" &&
                                ["state", "national", "international"].includes(String(r.event_level || "").toLowerCase()) &&
                                ((r.approval_stage || "District").toString().trim() !== "District" || r.status === "Approved"))
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed opacity-60"
                              : "bg-red-600 hover:bg-red-700 text-white shadow-sm"
                        }`}
                        title={
                          r.status === "Rejected"
                            ? "Already Rejected"
                            : roleLower === "district" &&
                              ["state", "national", "international"].includes(String(r.event_level || "").toLowerCase()) &&
                              (r.approval_stage || "District").toString().trim() !== "District"
                            ? "Cannot modify after forwarding to state"
                            : "Click to Reject"
                        }
                      >
                        {r.status === "Rejected" ? "✕ Rejected" : "Reject"}
                      </button>

                      {/* ID Card Download */}
                      {downloadingIds.has(r.registration_id) ? (
                        <span className="text-xs text-indigo-600 font-semibold flex items-center justify-center gap-1 min-w-[65px] select-none">
                          <span className="animate-spin inline-block w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full"></span>
                          Downloading
                        </span>
                      ) : (
                        <button
                          onClick={() => handleDownloadIdCard(r.registration_id, r.player_id, r.event_id)}
                          disabled={r.status !== "Approved"}
                          className={`px-2 py-1 rounded text-[11px] whitespace-nowrap ${
                            r.status === "Approved"
                              ? "bg-blue-600 hover:bg-blue-700 text-white"
                              : "bg-gray-300 text-gray-600 cursor-not-allowed opacity-60"
                          }`}
                        >
                          ID Card
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="relative z-10 flex justify-start sm:justify-center items-center gap-2 p-4 border-t bg-slate-50/50 overflow-x-auto whitespace-nowrap [scrollbar-width:thin]">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 rounded text-xs font-semibold border bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              ◀ Prev
            </button>
            {getPageNumbers(page, totalPages).map((pNum, idx) => {
              if (pNum === "...") {
                return (
                  <span key={`ell-${idx}`} className="px-2 text-slate-500 font-medium select-none">
                    ...
                  </span>
                );
              }
              return (
                <button
                  key={pNum}
                  onClick={() => setPage(pNum)}
                  className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                    page === pNum
                      ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                      : "bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {pNum}
                </button>
              );
            })}
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 rounded text-xs font-semibold border bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              Next ▶
            </button>
          </div>
        )}
        </section>

        {selectedGameReg && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-xl p-6 w-[90%] md:w-[420px] relative shadow-xl">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-red-600"
                onClick={() => setSelectedGameReg(null)}
              >
                ✕
              </button>

              <h3 className="text-xl font-semibold mb-3 text-center">
                {selectedGameReg.mode === "add" ? "Add game" : "Change game"} for {selectedGameReg.player_name}
              </h3>

              {selectedGameReg.mode !== "add" && <div className="mb-3">
                <label className="block text-sm mb-1">Current Game</label>
                  <input
                  type="text"
                  readOnly
                  value={
                    selectedGameReg.current_game_name ||
                    gameLabel(
                      (Array.isArray(availableGames) ? availableGames : [])
                        .find(g => String(g.id) === String(selectedGameReg.current_game_id))
                    )
                  }
                  className="w-full border p-2 rounded bg-gray-100"
                />
              </div>}

              {selectedGameReg.mode === "add" && (
                <p className="mb-3 text-xs text-slate-600">
                  Current games: {selectedGameReg.existing_game_ids?.length || 0} / {selectedGameReg.max_games}
                </p>
              )}

              <div className="mb-4">
                <label className="block text-sm mb-1">{selectedGameReg.mode === "add" ? "Select Game to Add" : "Select New Game"}</label>
                <SearchableSelect
                  value={newGameForReg}
                  onChange={(e) => setNewGameForReg(e.target.value)}
                  className="w-full border p-2 rounded"
                >
                  <option value="">-- choose game --</option>
                  {availableGames.map((g) => (
                    <option key={g.id} value={g.id}>
                      {gameLabel(g)}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveChangedSingleGame}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => setSelectedGameReg(null)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Escort Modal */}
        {selectedEscort && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center px-3"
            onClick={() => setSelectedEscort(null)}   // 👈 click outside closes
          >
            <div
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative shadow-xl"
              onClick={(e) => e.stopPropagation()}     // 👈 prevent close on modal click
            >
              {/* Close button (ALWAYS visible) */}
              <button
                className="sticky top-0 float-right m-3 text-gray-500 hover:text-red-600 text-xl font-bold z-10"
                onClick={() => setSelectedEscort(null)}
              >
                ✕
              </button>

              <div className="p-6 pt-2">
                <h2 className="text-xl font-semibold text-center mb-4 text-blue-700">
                  Change Escort / Guide Runner for {selectedEscort.player_name}
                </h2>

                {/* Escort 1 */}
                <h4 className="font-semibold mb-2">Escort / Guide Runner 1</h4>

                <input
                  type="text"
                  name="escort_name"
                  value={escortForm.escort_name}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner Name"
                  className="border p-2 rounded w-full mb-2"
                />
                <input
                  type="email"
                  name="escort_email"
                  value={escortForm.escort_email || ""}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner Email"
                  className="border p-2 rounded w-full mb-2"
                />

                <input
                  type="text"
                  name="escort_type"
                  value={escortForm.escort_type}
                  onChange={handleEscortChange}
                  placeholder="Guide Runner / Coach / Parent / Helper"
                  className="border p-2 rounded w-full mb-2"
                />

                {selectedEscort.escort_photo && (
                  <img
                    src={getImageUrl(selectedEscort.escort_photo)}
                    alt="Escort 1"
                    className="w-24 h-24 object-cover rounded border mb-2"
                  />
                )}

                <input
                  type="file"
                  name="escort_photo"
                  accept="image/*"
                  onChange={handleEscortChange}
                  className="border p-2 rounded w-full mb-4"
                />

                <hr className="my-4" />

                {/* Escort 2 */}
                <h4 className="font-semibold mb-2">Escort / Guide Runner 2</h4>

                <input
                  type="text"
                  name="escort_name_2"
                  value={escortForm.escort_name_2}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner 2 Name"
                  className="border p-2 rounded w-full mb-2"
                />
                <input
                  type="email"
                  name="escort_email_2"
                  value={escortForm.escort_email_2 || ""}
                  onChange={handleEscortChange}
                  placeholder="Escort or Guide Runner 2 Email"
                  className="border p-2 rounded w-full mb-2"
                />

                <input
                  type="text"
                  name="escort_type_2"
                  value={escortForm.escort_type_2}
                  onChange={handleEscortChange}
                  placeholder="Guide Runner / Coach / Parent / Helper"
                  className="border p-2 rounded w-full mb-2"
                />

                {selectedEscort.escort_photo_2 && (
                  <img
                    src={getImageUrl(selectedEscort.escort_photo_2)}
                    alt="Escort 2"
                    className="w-24 h-24 object-cover rounded border mb-2"
                  />
                )}

                <input
                  type="file"
                  name="escort_photo_2"
                  accept="image/*"
                  onChange={handleEscortChange}
                  className="border p-2 rounded w-full mb-6"
                />
                <div className="flex gap-2 mb-4">
                  {/* Remove Escort 1 */}
                  {escortForm.escort_name && (
                    <button
                      type="button"
                      onClick={async () => {
                        const result = await Swal.fire({
                          icon: "warning",
                          title: "Remove Escort 1?",
                          showCancelButton: true,
                          confirmButtonText: "Remove",
                        });
                        if (!result.isConfirmed) return;

                        const fd = new FormData();
                        fd.append("_method", "PUT");
                        fd.append("remove_escort_1", "1");

                        API.post(
                          `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
                          fd
                        ).then(() => {
                          setSelectedEscort(null);
                          loadData();
                        });
                      }}
                      className="flex-1 bg-red-100 text-red-700 py-2 rounded hover:bg-red-200"
                    >
                      Remove Escort 1
                    </button>
                  )}

                  {/* Remove Escort 2 */}
                  {escortForm.escort_name_2 && (
                    <button
                      type="button"
                      onClick={async () => {
                        const result = await Swal.fire({
                          icon: "warning",
                          title: "Remove Escort 2?",
                          showCancelButton: true,
                          confirmButtonText: "Remove",
                        });
                        if (!result.isConfirmed) return;

                        const fd = new FormData();
                        fd.append("_method", "PUT");
                        fd.append("remove_escort_2", "1");

                        API.post(
                          `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
                          fd
                        ).then(() => {
                          setSelectedEscort(null);
                          loadData();
                        });
                      }}
                      className="flex-1 bg-red-100 text-red-700 py-2 rounded hover:bg-red-200"
                    >
                      Remove Escort 2
                    </button>
                  )}
                </div>
                {(escortForm.escort_name || escortForm.escort_name_2) && (
                  <button
                    type="button"
                    onClick={async () => {
                      const result = await Swal.fire({
                        icon: "warning",
                        title: "Remove BOTH escorts?",
                        showCancelButton: true,
                        confirmButtonText: "Remove",
                      });
                      if (!result.isConfirmed) return;

                      const fd = new FormData();
                      fd.append("_method", "PUT");
                      fd.append("remove_escort_1", "1");
                      fd.append("remove_escort_2", "1");

                      API.post(
                        `/admin/registrations/${selectedEscort.registration_id}/update-escort`,
                        fd
                      ).then(() => {
                        setSelectedEscort(null);
                        loadData();
                      });
                    }}
                    className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 mb-4"
                  >
                    Remove Both Escorts
                  </button>
                )}
                <button
                  onClick={saveEscortDetails}
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                >
                  Save Escort Details
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 transition-opacity"
          onClick={() => setPreviewImage(null)} // click empty space closes
        >
          <div
            className="relative max-w-[90%] max-h-[90%]"
            onClick={(e) => e.stopPropagation()} // prevent close when clicking image
          >
            <img
              src={previewImage}
              alt="Preview"
              className="rounded-lg shadow-xl max-h-[90vh] max-w-[90vw] object-contain"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 bg-black bg-opacity-60 text-white rounded-full px-3 py-1 hover:bg-opacity-80"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EventRegistrations;
